import React, { useEffect, useMemo, useState } from 'react'
import ReactGridLayout, { Responsive, WidthProvider } from 'react-grid-layout'
import ChartCart1 from './Component/Card/IncomeCard'
import {
  Box,
  FormControl,
  InputBase,
  InputLabel,
  MenuItem,
  Select,
  styled,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material'
import SettingsIcon from '@mui/icons-material/Settings'

import TabCard from './Component/Card/TableCard'
import SimpleCard from './Component/Card/SimpleCard'
import './style.css'
import MonPatrimoineNet from './Component/Card/MonPatrimoineNet'
import { makeGetRequest, makePutRequest } from 'utils/api'
import { useSelector } from 'react-redux'
import { useDispatch } from 'react-redux'
import { setSitedevise } from 'reduxLib/slices/deviseSlice'

import MonBudgetCard from './Component/Card/MonBudget'
import { AppContext } from 'context'
import DashboardSettingsModal from './Component/DashboardSettingsModal'
import DashboardSettingsButton from './Component/DashboardSettingsButton'
import { filterReports } from 'utils/dashboardFilters'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

const ResponsiveGridLayout = WidthProvider(Responsive)

function MainPageFiState() {
  const { dispatchAccountClick } = React.useContext(AppContext)
  const { t } = useTranslation('layout') // LAP-733 (GE022)

  // const ResponsiveGridLayout = useMemo(() => WidthProvider(Responsive), [])
  const dispatch = useDispatch()
  const siteDevise = useSelector((store) => store.devise)
  const user = useSelector((store) => store.user.user)
  const theme = useTheme()
  const [reports, setReports] = useState([])
  // const [devise, setDevise] = useState(siteDevise.devise)
  const [deviseList, setDeviseList] = useState([])
  const [selectedCurrency, setSelectedCurrency] = useState(null)
  const [settingsModalOpen, setSettingsModalOpen] = useState(false)
  const [dashboardSettings, setDashboardSettings] = useState({
    excluded_categories: [],
    excluded_subcategories: [],
    layout_preferences: {
      hidden_cards: [],
      card_order: [],
    },
  })
  
  useEffect(() => {
    getDeviseList()
    loadDashboardSettings()
    dispatchAccountClick('CLICK', { id: '' })
  }, [])

  useEffect(() => {
    if (deviseList.length > 0 && user?.currency_code) {
      const selected = deviseList.find((d) => d.Code_iso === user.currency_code)
      if (selected) {
        dispatch(setSitedevise(selected))
        setSelectedCurrency(selected.Code_iso)
      }
    }
  }, [deviseList, user, dispatch])

  // Refetch reports when currency changes
  useEffect(() => {
    if (selectedCurrency) {
      getReports(selectedCurrency)
    }
  }, [selectedCurrency])

  const getReports = async (currency) => {
    const currencyParam = currency ? `?currency=${currency}` : ''
    const { data, status } = await makeGetRequest(`/api/Finance/reports/${currencyParam}`, {}, true)
    // Robustesse : si l'endpoint renvoie une erreur (500) ou autre chose qu'un
    // tableau, on n'essaie pas de filtrer (ça planterait silencieusement et
    // laisserait le dashboard vide). On loggue et on affiche un état vide propre.
    if (status !== 200 || !Array.isArray(data)) {
      console.error('[fiStatement] /reports/ a échoué', status, data)
      setReports([])
      return
    }
    const filteredData = filterReports(data, dashboardSettings)
    setReports([...(Array.isArray(filteredData) ? filteredData : [])])
  }
  
  const getDeviseList = async () => {
    const response = await makeGetRequest('/api/Finance/devise-list/', {}, true)
    if (response.status === 200) {
      setDeviseList(response.data)
    }
  }

  const loadDashboardSettings = async () => {
    try {
      const response = await makeGetRequest('/api/Finance/dashboard-settings/', {}, true)
      if (response.data) {
        const layoutPrefs = response.data.layout_preferences || {}
        setDashboardSettings({
          excluded_categories: response.data.excluded_categories || [],
          excluded_subcategories: response.data.excluded_subcategories || [],
          layout_preferences: {
            hidden_cards: layoutPrefs.hidden_cards || [],
            card_order: layoutPrefs.card_order || [],
          },
        })
      }
    } catch (error) {
      console.error('Error loading dashboard settings:', error)
    }
  }

  const handleCurrencyChange = (newCurrency) => {
    setSelectedCurrency(newCurrency.Code_iso)
    dispatch(setSitedevise(newCurrency))
  }

  const handleSettingsSave = (newSettings) => {
    setDashboardSettings(newSettings)
    // Refresh reports with new settings
    if (selectedCurrency) {
      getReports(selectedCurrency)
    }
  }

  const saveCardOrder = async (layout) => {
    console.log('=== saveCardOrder called ===')
    console.log('Raw layout:', layout)
    
    try {
      // Sort layout by position (y then x) to get visual order
      const sortedLayout = [...layout].sort((a, b) => {
        if (a.y === b.y) {
          return a.x - b.x
        }
        return a.y - b.y
      })

      console.log('Sorted layout:', sortedLayout)

      // Extract card IDs from sorted layout
      const cardOrder = sortedLayout
        .map(item => {
          // Extract card ID from item.i
          // React adds .$ prefix, so we need to extract the number
          // Format is like: '.$1-USD' or '1-USD' or just '1'
          const itemKey = item.i.toString()
          
          // Try to extract number from the key
          const match = itemKey.match(/\d+/)
          const cardId = match ? parseInt(match[0]) : null
          
          console.log('Processing item:', item.i, '-> extracted:', match, '-> cardId:', cardId)
          return cardId
        })
        .filter(id => id !== null && !isNaN(id))

      console.log('Extracted card order:', cardOrder)

      // Update settings with new card order
      const updatedSettings = {
        ...dashboardSettings,
        layout_preferences: {
          ...dashboardSettings.layout_preferences,
          card_order: cardOrder,
        },
      }

      console.log('Settings to save:', updatedSettings)

      // Save to backend
      const response = await makePutRequest('/api/Finance/dashboard-settings/0/', updatedSettings, {}, true)
      console.log('Save response:', response)
      
      // Update local state
      setDashboardSettings(updatedSettings)
      
      console.log('✅ Card order saved successfully:', cardOrder)
    } catch (error) {
      console.error('❌ Error saving card order:', error)
    }
  }

  const handleLayoutChange = (layout, layouts) => {
    // Only save on drag end, not on every layout change
    // Check if this is from user interaction (has proper layout structure)
    if (layout && layout.length > 0 && layout[0].i) {
      console.log('Layout changed, saving order...', layout)
      saveCardOrder(layout)
    }
  }

  // Get current layout from grid
  const getCurrentLayout = () => {
    // This would capture the current layout from ReactGridLayout
    // For now, return the saved layout
    return dashboardSettings.layout_preferences.card_order
  }

  // Filter and order children based on settings
  const getVisibleChildren = () => {
    const hiddenCards = dashboardSettings?.layout_preferences?.hidden_cards || []
    const cardOrder = dashboardSettings?.layout_preferences?.card_order || []
    
    const childrenArray = React.Children.toArray(children)
    
    // Filter out hidden cards
    let visibleChildren = childrenArray.filter((child) => {
      if (!child || !child.key) {
        return true
      }
      
      const childKey = child.key.toString()
      // Check if child is hidden by card ID
      const isHidden = hiddenCards.some((hiddenId) => {
        return childKey.includes(String(hiddenId))
      })
      
      return !isHidden
    })
    
    // Apply ordering if card_order is defined
    if (cardOrder && cardOrder.length > 0) {
      const orderedChildren = []
      const childrenByKey = {}
      
      // Create a map of children by their keys
      visibleChildren.forEach((child) => {
        if (child && child.key) {
          childrenByKey[child.key] = child
        }
      })
      
      // Order children according to card_order
      cardOrder.forEach((cardId) => {
        // Find child that matches this card ID
        const matchingChild = Object.entries(childrenByKey).find(([key, child]) => {
          return key.includes(String(cardId))
        })
        
        if (matchingChild) {
          orderedChildren.push(matchingChild[1])
          delete childrenByKey[matchingChild[0]]
        }
      })
      
      // Add any remaining children that weren't in the order
      Object.values(childrenByKey).forEach((child) => {
        orderedChildren.push(child)
      })
      
      return orderedChildren
    }
    
    return visibleChildren
  }

  const layout1 = [
    { i: '1', x: 0, y: 0, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '2', x: 3, y: 0, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '3', x: 6, y: 0, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '4', x: 9, y: 0, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '5', x: 0, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '6', x: 3, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '7', x: 6, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '8', x: 9, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
  ]
  const layout2 = [
    { i: '1', x: 0, y: 0, w: 5, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '2', x: 5, y: 0, w: 5, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '3', x: 0, y: 0, w: 5, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '4', x: 5, y: 0, w: 5, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '5', x: 0, y: 5, w: 5, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '6', x: 5, y: 5, w: 5, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '7', x: 0, y: 5, w: 5, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '8', x: 5, y: 5, w: 5, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
  ]
  const layout3 = [
    { i: '1', x: 0, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '2', x: 3, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '3', x: 0, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '4', x: 3, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '5', x: 0, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '6', x: 3, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '7', x: 0, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '8', x: 3, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
  ]
  const layout4 = [
    { i: '1', x: 0, y: 5, w: 4, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '2', x: 0, y: 5, w: 4, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '3', x: 0, y: 5, w: 4, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '4', x: 0, y: 5, w: 4, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '5', x: 0, y: 5, w: 4, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '6', x: 0, y: 5, w: 4, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '7', x: 0, y: 5, w: 4, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '8', x: 0, y: 5, w: 4, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
  ]
  const layouts = { lg: layout1, md: layout2, sm: layout3, xs: layout4 }

  const children = React.useMemo(() => {
    const xArr = [0, 3, 6, 9, 0, 3, 6, 9, 0, 3, 6, 9, 0, 3, 6, 9, 0, 3, 6, 9, 0]
    
    // Apply saved card order if available
    const cardOrder = dashboardSettings?.layout_preferences?.card_order || []
    let orderedReports = [...(reports || [])]
    
    if (cardOrder && cardOrder.length > 0) {
      // Reorder reports based on saved card_order
      orderedReports = []
      cardOrder.forEach(cardId => {
        const report = reports.find(r => r.id === cardId)
        if (report) {
          orderedReports.push(report)
        }
      })
      // Add any new reports not in saved order
      reports.forEach(report => {
        if (!cardOrder.includes(report.id)) {
          orderedReports.push(report)
        }
      })
    }
    
    return (
      orderedReports &&
      orderedReports.map((val, idx) => (
        <div
          className="ch-div"
          key={`${val.id}-${selectedCurrency}`}
          style={{ backgroundColor: theme.palette.appBar.main, display: 'grid' }}
          data-grid={{
            i: String(val.id),  // Important: set 'i' to card ID for ReactGridLayout
            x: xArr[idx],
            y: Infinity,
            w: 3,
            h: 3,
            minW: 2,
            maxW: 8,
            minH: 3,
            maxH: 10,
          }}
        >
          {val.calcul !== 'balance.js' &&
            val.calcul !== 'patrione.js' &&
            val.calcul !== 'liquide.js' &&
            val.calcul !== 'budget.js' && <ChartCart1 oldData={val} currency={selectedCurrency} />}
          {val.calcul === 'balance.js' && <TabCard oldData={val} />}
          {val.calcul === 'patrimoine.js' && <SimpleCard oldData={val} />}
          {val.calcul === 'liquide.js' && <MonPatrimoineNet oldData={val?.calculation} />}
          {val.calcul === 'budget.js' && <MonBudgetCard oldData={val} />}
        </div>
      ))
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports, selectedCurrency, dashboardSettings.layout_preferences.card_order])
  const handleChangeDevise = () => {}
  const BootstrapInput = styled(InputBase)(({ theme }) => ({
    '& .MuiInputBase-input': {
      borderRadius: 4,
      position: 'relative',
      backgroundColor: theme.palette.background.paper,
      border: '1px solid #ced4da',
      fontSize: 16,
      padding: '3px 26px 3px 5px',
      transition: theme.transitions.create(['border-color', 'box-shadow']),

      '&:focus': {
        borderRadius: 4,
        borderColor: '#80bdff',
        boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
      },
    },
  }))
  return (
    <>
      <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mr={10} ml={10}>
        <Box>
          <DashboardSettingsButton onClick={() => setSettingsModalOpen(true)} />
        </Box>
        <Box sx={{ maxWidth: 240 }}>
          <FormControl fullWidth size="small">
            {/* <InputLabel>Devise</InputLabel> */}
            <Select
              value={siteDevise.devise.Code_iso}
              label={t('finance.fiStatement.currency')}
              // onChange={()=>setDevise()}
              input={<BootstrapInput />}
            >
              {deviseList.map((e) => (
                <MenuItem key={e.id} value={e.Code_iso} onClick={() => handleCurrencyChange(e)}>
                  {e.Devise} - {e.Description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box color={'white'}>{t('finance.fiStatement.currency')}</Box>
        </Box>
      </Box>
      <Box maxWidth={1650} ml={'auto'} mr={'auto'}>
        <ResponsiveGridLayout
          className="layout"
          style={{ margin: '10px' }}
          // autoSize={true}
          layouts={layouts}
          useCSSTransforms={true}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
          onDragStop={handleLayoutChange}
          // isDraggable={isDraggable}
          draggableCancel=".no-drag"
        >
          {getVisibleChildren()}
        </ResponsiveGridLayout>
      </Box>

      <DashboardSettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        onSave={handleSettingsSave}
        currentLayout={getCurrentLayout()}
      />
    </>
  )
}

export default MainPageFiState
